<template>
  <div>
    <v-container fluid tag="section" class="text_google">
      <v-row>
        <v-col cols="12">
          <base-material-card color="primary">
            <template v-slot:heading>
              <h2 class="h1 font-weight-light text_google">
                <v-icon large left>mdi-cog-transfer</v-icon>
                ประมวลผลอัตรากำลัง (Admin) — ทุกสถานศึกษา
                <span class="body-1 ml-3 yellow--text" v-if="yearCalNow"
                  >ปีการศึกษา {{ yearCalNow }}</span
                >
              </h2>
            </template>

            <!-- Summary Cards -->
            <v-row class="mb-2 mt-2 px-2">
              <v-col cols="6" md="2">
                <v-card class="pa-3 text-center" outlined>
                  <div class="text-h4 font-weight-bold primary--text">
                    {{ colleges.length }}
                  </div>
                  <div class="caption grey--text">สถานศึกษาทั้งหมด</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="2">
                <v-card class="pa-3 text-center" outlined>
                  <div class="text-h4 font-weight-bold success--text">
                    {{ confirmedCount }}
                  </div>
                  <div class="caption grey--text">ยืนยันแล้ว</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="2">
                <v-card class="pa-3 text-center" outlined>
                  <div class="text-h4 font-weight-bold info--text">
                    {{ calculatedCount }}
                  </div>
                  <div class="caption grey--text">คำนวณแล้ว (รอยืนยัน)</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="2">
                <v-card class="pa-3 text-center" outlined>
                  <div class="text-h4 font-weight-bold error--text">
                    {{ errorCount }}
                  </div>
                  <div class="caption grey--text">มีข้อผิดพลาด</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="2">
                <v-card class="pa-3 text-center" outlined>
                  <div class="text-h4 font-weight-bold warning--text">
                    {{ pendingCount }}
                  </div>
                  <div class="caption grey--text">รอดำเนินการ</div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Criterion Summary (บทสรุปขาด/เกินเกณฑ์) -->
            <v-row class="mb-4 px-2">
              <v-col cols="4">
                <v-card class="pa-3 text-center" outlined color="red lighten-5">
                  <div
                    class="text-h5 font-weight-bold red--text text--darken-2"
                  >
                    {{ criterionStats.shortage }}
                  </div>
                  <div class="caption red--text text--darken-2">
                    สถานศึกษาที่ "ขาด" อัตรากำลังตามเกณฑ์
                  </div>
                </v-card>
              </v-col>
              <v-col cols="4">
                <v-card
                  class="pa-3 text-center"
                  outlined
                  color="blue-grey lighten-5"
                >
                  <div
                    class="text-h5 font-weight-bold blue-grey--text text--darken-2"
                  >
                    {{ criterionStats.balanced }}
                  </div>
                  <div class="caption blue-grey--text text--darken-2">
                    สถานศึกษาที่ "พอดี" เกณฑ์
                  </div>
                </v-card>
              </v-col>
              <v-col cols="4">
                <v-card
                  class="pa-3 text-center"
                  outlined
                  color="green lighten-5"
                >
                  <div
                    class="text-h5 font-weight-bold green--text text--darken-2"
                  >
                    {{ criterionStats.surplus }}
                  </div>
                  <div class="caption green--text text--darken-2">
                    สถานศึกษาที่ "เกิน" อัตรากำลังตามเกณฑ์
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Progress Bars -->
            <v-card class="mb-4 pa-4" outlined>
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="subtitle-1 font-weight-medium"
                  >ความคืบหน้าการคำนวณ</span
                >
                <span class="subtitle-1"
                  >{{ calcProgressPercent }}% ({{
                    calculatedCount + confirmedCount + errorCount
                  }}
                  / {{ colleges.length }})</span
                >
              </div>
              <v-progress-linear
                :value="calcProgressPercent"
                height="16"
                rounded
                :color="
                  isRunning && batchMode === 'calc'
                    ? 'blue'
                    : errorCount > 0
                    ? 'orange'
                    : 'info'
                "
                background-color="grey lighten-3"
              >
                <template v-slot:default="{ value }"
                  ><strong>{{ Math.round(value) }}%</strong></template
                >
              </v-progress-linear>

              <div class="d-flex justify-space-between align-center mb-2 mt-4">
                <span class="subtitle-1 font-weight-medium"
                  >ความคืบหน้าการยืนยันข้อมูล</span
                >
                <span class="subtitle-1"
                  >{{ confirmProgressPercent }}% ({{ confirmedCount }} /
                  {{ colleges.length }})</span
                >
              </div>
              <v-progress-linear
                :value="confirmProgressPercent"
                height="16"
                rounded
                :color="
                  isRunning && batchMode === 'confirm' ? 'blue' : 'success'
                "
                background-color="grey lighten-3"
              >
                <template v-slot:default="{ value }"
                  ><strong>{{ Math.round(value) }}%</strong></template
                >
              </v-progress-linear>

              <div v-if="currentCollegeName" class="mt-2 caption blue--text">
                <v-icon small color="blue">mdi-loading mdi-spin</v-icon>
                {{ batchMode === "confirm" ? "กำลังยืนยัน" : "กำลังประมวลผล" }}:
                <strong>{{ currentCollegeName }}</strong>
                <v-chip
                  x-small
                  class="ml-2"
                  color="blue lighten-5"
                  text-color="blue darken-3"
                  >{{ currentStep }}</v-chip
                >
              </div>
              <div v-if="isRunning && etaText" class="mt-1 caption grey--text">
                เวลาที่เหลือโดยประมาณ: {{ etaText }}
              </div>
            </v-card>

            <!-- Pipeline Legend -->
            <v-card class="mb-4 pa-3" outlined color="grey lighten-5">
              <div
                class="caption font-weight-bold mb-1 grey--text text--darken-2"
              >
                ขั้นตอนการประมวลผลแต่ละสถานศึกษา
              </div>
              <div class="d-flex flex-wrap align-center">
                <v-chip x-small color="teal" dark class="mr-1">1</v-chip>
                <span class="caption mr-4"
                  >บันทึกอัตรากำลังบุคลากร (rate_work_g)</span
                >
                <v-icon small class="mr-1 grey--text">mdi-arrow-right</v-icon>
                <v-chip x-small color="blue" dark class="mr-1">2</v-chip>
                <span class="caption mr-4">ประมวลผลห้องเรียน + หมวดสามัญ</span>
                <v-icon small class="mr-1 grey--text">mdi-arrow-right</v-icon>
                <v-chip x-small color="purple" dark class="mr-1">3</v-chip>
                <span class="caption mr-4">คำนวณอัตรากำลัง (บันทึก Save)</span>
                <v-icon small class="mr-1 grey--text">mdi-arrow-right</v-icon>
                <v-chip x-small color="green" dark class="mr-1">4</v-chip>
                <span class="caption">ยืนยันการบันทึกข้อมูล (Confirm)</span>
              </div>
            </v-card>

            <!-- Control Buttons -->
            <v-row class="mb-2 px-2">
              <v-col>
                <v-btn
                  large
                  rounded
                  color="blue darken-2"
                  dark
                  :disabled="isRunning || colleges.length === 0"
                  :loading="loadingColleges"
                  @click="startBatchAll"
                >
                  <v-icon left>mdi-play-circle</v-icon>
                  คำนวณทั้งหมด ({{ colleges.length }} แห่ง)
                </v-btn>
                <v-btn
                  large
                  rounded
                  color="orange darken-2"
                  dark
                  :disabled="isRunning || pendingCalcOnly.length === 0"
                  class="ml-2"
                  @click="startBatchPending"
                >
                  <v-icon left>mdi-reload</v-icon>
                  เฉพาะที่ยังไม่คำนวณ ({{ pendingCalcOnly.length }})
                </v-btn>
                <v-btn
                  large
                  rounded
                  color="green darken-2"
                  dark
                  :disabled="isRunning || pendingConfirmOnly.length === 0"
                  class="ml-2"
                  @click="startBatchConfirmAll"
                >
                  <v-icon left>mdi-checkbox-marked-circle</v-icon>
                  ยืนยันข้อมูลที่คำนวณแล้ว ({{ pendingConfirmOnly.length }})
                </v-btn>
                <v-btn
                  large
                  rounded
                  color="red darken-2"
                  dark
                  :disabled="!isRunning"
                  class="ml-2"
                  @click="stopBatch"
                >
                  <v-icon left>mdi-stop-circle</v-icon>หยุด
                </v-btn>
                <v-btn
                  large
                  rounded
                  outlined
                  class="ml-2"
                  :disabled="isRunning"
                  @click="resetAll"
                >
                  <v-icon left>mdi-refresh</v-icon>รีเซ็ต
                </v-btn>
              </v-col>
            </v-row>
            <v-row class="mb-4 px-2">
              <v-col>
                <v-switch
                  v-model="autoConfirm"
                  :disabled="isRunning"
                  color="green"
                  inset
                  hide-details
                  label="ยืนยันข้อมูลอัตโนมัติทันทีหลังคำนวณเสร็จ (รวม Step 4 เข้ากับ Step 1-3)"
                />
              </v-col>
            </v-row>

            <!-- Table -->
            <v-card outlined>
              <v-card-title class="py-2">
                <v-text-field
                  v-model="searchCollege"
                  prepend-inner-icon="mdi-magnify"
                  label="ค้นหาสถานศึกษา"
                  single-line
                  hide-details
                  dense
                  outlined
                  style="max-width:320px"
                />
                <v-spacer />
                <v-btn-toggle v-model="filterStatus" mandatory dense>
                  <v-btn small value="all">ทั้งหมด</v-btn>
                  <v-btn small value="confirmed">ยืนยันแล้ว</v-btn>
                  <v-btn small value="calculated">คำนวณแล้ว</v-btn>
                  <v-btn small value="error">ผิดพลาด</v-btn>
                  <v-btn small value="pending">รอดำเนินการ</v-btn>
                </v-btn-toggle>
              </v-card-title>

              <v-data-table
                :headers="tableHeaders"
                :items="filteredColleges"
                :loading="loadingColleges"
                :search="searchCollege"
                :items-per-page="20"
                dense
                fixed-header
                height="520px"
              >
                <template v-slot:item.steps="{ item }">
                  <div class="d-flex align-center">
                    <v-icon
                      x-small
                      :color="stepColor(item.step1)"
                      class="mr-1"
                      :title="'Step 1: ' + item.step1"
                    >
                      {{
                        item.step1 === "ok"
                          ? "mdi-numeric-1-box"
                          : "mdi-numeric-1-box-outline"
                      }}
                    </v-icon>
                    <v-icon
                      x-small
                      :color="stepColor(item.step2)"
                      class="mr-1"
                      :title="'Step 2: ' + item.step2"
                    >
                      {{
                        item.step2 === "ok"
                          ? "mdi-numeric-2-box"
                          : "mdi-numeric-2-box-outline"
                      }}
                    </v-icon>
                    <v-icon
                      x-small
                      :color="stepColor(item.step3)"
                      class="mr-1"
                      :title="'Step 3: ' + item.step3"
                    >
                      {{
                        item.step3 === "ok"
                          ? "mdi-numeric-3-box"
                          : "mdi-numeric-3-box-outline"
                      }}
                    </v-icon>
                    <v-icon
                      x-small
                      :color="stepColor(item.step4)"
                      :title="'Step 4: ' + item.step4"
                    >
                      {{
                        item.step4 === "ok"
                          ? "mdi-numeric-4-box"
                          : "mdi-numeric-4-box-outline"
                      }}
                    </v-icon>
                  </div>
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip
                    v-if="item.status === 'confirmed'"
                    small
                    color="success"
                    text-color="white"
                  >
                    <v-icon left x-small>mdi-check-decagram</v-icon>ยืนยันแล้ว
                  </v-chip>
                  <v-chip
                    v-else-if="item.status === 'calculated'"
                    small
                    color="info"
                    text-color="white"
                  >
                    <v-icon left x-small>mdi-check-circle-outline</v-icon
                    >คำนวณแล้ว รอยืนยัน
                  </v-chip>
                  <v-chip
                    v-else-if="item.status === 'error'"
                    small
                    color="error"
                    text-color="white"
                  >
                    <v-icon left x-small>mdi-alert-circle</v-icon>ผิดพลาด
                  </v-chip>
                  <v-chip
                    v-else-if="item.status === 'running'"
                    small
                    color="blue"
                    text-color="white"
                  >
                    <v-icon left x-small>mdi-loading mdi-spin</v-icon
                    >กำลังดำเนินการ
                  </v-chip>
                  <v-chip
                    v-else
                    small
                    color="grey lighten-2"
                    text-color="grey darken-2"
                  >
                    <v-icon left x-small>mdi-clock-outline</v-icon>รอดำเนินการ
                  </v-chip>
                </template>

                <template v-slot:item.college_id_code_type_manpower="{ item }">
                  <v-chip x-small outlined>{{
                    item.college_id_code_type_manpower
                  }}</v-chip>
                </template>

                <template v-slot:item.manpowerAll="{ item }">
                  <span v-if="item.manpowerAll !== null">{{
                    item.manpowerAll
                  }}</span>
                  <span v-else class="grey--text">—</span>
                </template>

                <template v-slot:item.criterionAll="{ item }">
                  <span v-if="item.criterionAll !== null">{{
                    item.criterionAll
                  }}</span>
                  <span v-else class="grey--text">—</span>
                </template>

                <template v-slot:item.criterionResult="{ item }">
                  <v-chip
                    v-if="item.incompleteAll !== null"
                    x-small
                    :color="criterionColor(item.incompleteAll)"
                    text-color="white"
                  >
                    {{ criterionLabel(item.incompleteAll) }}
                  </v-chip>
                  <span v-else class="grey--text">—</span>
                </template>

                <template v-slot:item.duration="{ item }">
                  <span v-if="item.duration">{{ item.duration }}s</span>
                  <span v-else class="grey--text">—</span>
                </template>

                <template v-slot:item.errorMsg="{ item }">
                  <span v-if="item.errorMsg" class="error--text caption">{{
                    item.errorMsg
                  }}</span>
                  <span v-else class="grey--text">—</span>
                </template>

                <template v-slot:item.action="{ item }">
                  <v-btn
                    x-small
                    icon
                    color="blue"
                    :disabled="isRunning"
                    @click="processSingleCollege(item)"
                    title="คำนวณใหม่ (Step 1-3)"
                  >
                    <v-icon x-small>mdi-reload</v-icon>
                  </v-btn>
                  <v-btn
                    x-small
                    icon
                    color="green"
                    :disabled="isRunning || item.status !== 'calculated'"
                    @click="confirmSingleCollege(item)"
                    title="ยืนยันการบันทึกข้อมูล (Step 4)"
                  >
                    <v-icon x-small>mdi-check-bold</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card>

            <!-- Log -->
            <v-expansion-panels class="mt-4" flat>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <span
                    ><v-icon small left>mdi-text-box-outline</v-icon>Log
                    การประมวลผล ({{ logs.length }} รายการ)</span
                  >
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card
                    outlined
                    style="max-height:300px;overflow-y:auto;background:#1a1a1a;font-family:monospace;font-size:12px;"
                    class="pa-3"
                  >
                    <div
                      v-for="(log, i) in logs.slice().reverse()"
                      :key="i"
                      :class="logColor(log.type)"
                    >
                      <span class="grey--text">[{{ log.time }}]</span>
                      {{ log.msg }}
                    </div>
                    <div v-if="!logs.length" class="grey--text">
                      ยังไม่มี log
                    </div>
                  </v-card>
                  <v-btn x-small text class="mt-2" @click="logs = []">
                    <v-icon x-small left>mdi-delete</v-icon>ล้าง log
                  </v-btn>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </base-material-card>
        </v-col>
      </v-row>

      <v-snackbar
        v-model="snackbar.show"
        top
        :timeout="5000"
        :color="snackbar.color"
      >
        <v-icon left>{{ snackbar.icon }}</v-icon
        >{{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
export default {
  name: "RateWorkforceProAdmin",

  data() {
    return {
      ApiKey: "HRvec2021",
      user: {},
      colleges: [],
      collegeStatuses: {},
      isRunning: false,
      stopRequested: false,
      loadingColleges: false,
      autoConfirm: false,
      batchMode: "calc",
      currentCollegeName: "",
      currentStep: "",
      currentIndex: 0,
      batchQueue: [],
      batchStartTime: null,
      searchCollege: "",
      filterStatus: "all",
      logs: [],
      period_colleges: {},
      snackbar: { show: false, color: "", icon: "", text: "" },
      tableHeaders: [
        { text: "#", value: "index", width: 50 },
        { text: "รหัส", value: "user_code", width: 110 },
        { text: "ชื่อสถานศึกษา", value: "college_name" },
        { text: "ประเภท", value: "college_id_code_type_manpower", width: 90 },
        { text: "ขั้นตอน", value: "steps", width: 100, sortable: false },
        { text: "สถานะ", value: "status", width: 155 },
        { text: "อัตรากำลังจริง", value: "manpowerAll", width: 110 },
        { text: "เกณฑ์รวม", value: "criterionAll", width: 100 },
        {
          text: "ผลเกณฑ์",
          value: "criterionResult",
          width: 110,
          sortable: false
        },
        { text: "เวลา(s)", value: "duration", width: 80 },
        { text: "ข้อผิดพลาด", value: "errorMsg", width: 200 },
        { text: "ดำเนินการ", value: "action", sortable: false, width: 90 }
      ]
    };
  },

  async mounted() {
    await this.sweetAlertLoading();
    await this.loadCurrentUser();
    await this.period_collegeQuery();
    await this.loadColleges();
    Swal.close();
  },

  computed: {
    confirmedCount() {
      return this.colleges.filter(
        c => this.getStatus(c.user_code) === "confirmed"
      ).length;
    },
    calculatedCount() {
      return this.colleges.filter(
        c => this.getStatus(c.user_code) === "calculated"
      ).length;
    },
    errorCount() {
      return this.colleges.filter(c => this.getStatus(c.user_code) === "error")
        .length;
    },
    pendingCount() {
      return this.colleges.filter(
        c =>
          !["calculated", "confirmed", "error"].includes(
            this.getStatus(c.user_code)
          )
      ).length;
    },
    calcProgressPercent() {
      if (!this.colleges.length) return 0;
      return Math.round(
        ((this.calculatedCount + this.confirmedCount + this.errorCount) /
          this.colleges.length) *
          100
      );
    },
    confirmProgressPercent() {
      if (!this.colleges.length) return 0;
      return Math.round((this.confirmedCount / this.colleges.length) * 100);
    },
    pendingCalcOnly() {
      return this.colleges.filter(
        c => !["calculated", "confirmed"].includes(this.getStatus(c.user_code))
      );
    },
    pendingConfirmOnly() {
      return this.colleges.filter(
        c => this.getStatus(c.user_code) === "calculated"
      );
    },
    criterionStats() {
      let shortage = 0,
        balanced = 0,
        surplus = 0;
      this.colleges.forEach(c => {
        const st = this.collegeStatuses[c.user_code];
        if (!st || st.incompleteAll === undefined || st.incompleteAll === null)
          return;
        if (st.incompleteAll < 0) shortage++;
        else if (st.incompleteAll > 0) surplus++;
        else balanced++;
      });
      return { shortage, balanced, surplus };
    },
    etaText() {
      if (!this.isRunning || !this.batchStartTime || !this.currentIndex)
        return "";
      const elapsed = (Date.now() - this.batchStartTime) / 1000;
      const rate = this.currentIndex / elapsed;
      if (!rate) return "";
      const rem = (this.batchQueue.length - this.currentIndex) / rate;
      return rem < 60
        ? `${Math.round(rem)} วินาที`
        : `${Math.round(rem / 60)} นาที`;
    },
    filteredColleges() {
      return this.colleges
        .map((c, i) => {
          const st = this.collegeStatuses[c.user_code] || {};
          return {
            ...c,
            index: i + 1,
            status: st.status || "pending",
            step1: st.step1 || "pending",
            step2: st.step2 || "pending",
            step3: st.step3 || "pending",
            step4: st.step4 || "pending",
            duration: st.duration || null,
            errorMsg: st.errorMsg || null,
            criterionAll:
              st.criterionAll === undefined ? null : st.criterionAll,
            manpowerAll: st.manpowerAll === undefined ? null : st.manpowerAll,
            incompleteAll:
              st.incompleteAll === undefined ? null : st.incompleteAll
          };
        })
        .filter(c => {
          if (this.filterStatus === "confirmed")
            return c.status === "confirmed";
          if (this.filterStatus === "calculated")
            return c.status === "calculated";
          if (this.filterStatus === "error") return c.status === "error";
          if (this.filterStatus === "pending")
            return !["calculated", "confirmed", "error"].includes(c.status);
          return true;
        });
    },
    yearCalNow() {
      return this.period_colleges.period_college_year;
    },
    dateToday() {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;
    }
  },

  methods: {
    getStatus(code) {
      return this.collegeStatuses[code]?.status || "pending";
    },
    setStatus(code, patch) {
      this.$set(this.collegeStatuses, code, {
        ...(this.collegeStatuses[code] || {}),
        ...patch
      });
    },
    addLog(msg, type = "info") {
      const now = new Date();
      const time = [now.getHours(), now.getMinutes(), now.getSeconds()]
        .map(n => String(n).padStart(2, "0"))
        .join(":");
      this.logs.push({ msg, type, time });
      if (this.logs.length > 600) this.logs.shift();
    },
    logColor(t) {
      if (t === "success") return "green--text text--lighten-2";
      if (t === "error") return "red--text text--lighten-2";
      if (t === "warn") return "yellow--text text--lighten-2";
      if (t === "step") return "cyan--text text--lighten-2";
      return "grey--text text--lighten-1";
    },
    stepColor(s) {
      if (s === "ok") return "teal";
      if (s === "err") return "red";
      return "grey lighten-1";
    },
    criterionColor(v) {
      if (v === null || v === undefined) return "grey lighten-2";
      if (v < 0) return "red";
      if (v > 0) return "blue";
      return "green";
    },
    criterionLabel(v) {
      if (v === null || v === undefined) return "—";
      if (v < 0) return `ขาด ${Math.abs(v)}`;
      if (v > 0) return `เกิน ${v}`;
      return "พอดีเกณฑ์";
    },
    sleep(ms) {
      return new Promise(r => setTimeout(r, ms));
    },
    int(v) {
      return parseInt(v) || 0;
    },
    flt(v) {
      return parseFloat(v) || 0;
    },

    async sweetAlertLoading() {
      Swal.fire({
        title: "Please Wait !",
        timerProgressBar: true,
        didOpen: () => Swal.showLoading(),
        allowOutsideClick: false,
        allowEscapeKey: false
      });
    },
    async loadCurrentUser() {
      const r = await this.$http.post("user.php", {
        ApiKey: this.ApiKey,
        user_admin: "Ok"
      });
      this.user = r.data;
    },
    async period_collegeQuery() {
      const r = await this.$http.post("period_college.php", {
        ApiKey: this.ApiKey,
        period_college_enable: "1",
        period_college_type: "update_college"
      });
      this.period_colleges = r.data;
    },
    async loadColleges() {
      this.loadingColleges = true;
      try {
        const r = await this.$http.post("user.php", {
          ApiKey: this.ApiKey,
          user_admin: "Ok"
        });
        const raw = Array.isArray(r.data) ? r.data : [];
        this.colleges = raw.filter(
          u => u.user_code && u.college_name && u.college_id_code_type_manpower
        );
        this.addLog(
          `โหลดสถานศึกษาสำเร็จ ${this.colleges.length} แห่ง`,
          "success"
        );
      } catch (e) {
        this.addLog(`โหลดสถานศึกษาผิดพลาด: ${e.message}`, "error");
      } finally {
        this.loadingColleges = false;
      }
    },

    startBatchAll() {
      this.batchQueue = [...this.colleges];
      this.runBatch(this.batchQueue, "calc");
    },
    startBatchPending() {
      this.batchQueue = [...this.pendingCalcOnly];
      this.runBatch(this.batchQueue, "calc");
    },
    startBatchConfirmAll() {
      this.batchQueue = [...this.pendingConfirmOnly];
      this.runBatch(this.batchQueue, "confirm");
    },
    stopBatch() {
      this.stopRequested = true;
      this.addLog("ผู้ใช้สั่งหยุดการดำเนินการ", "warn");
    },
    resetAll() {
      this.collegeStatuses = {};
      this.logs = [];
      this.currentCollegeName = "";
      this.currentStep = "";
      this.currentIndex = 0;
      this.batchMode = "calc";
      this.addLog("รีเซ็ตสถานะทั้งหมดแล้ว", "info");
    },
    async runBatch(queue, mode = "calc") {
      if (this.isRunning) return;
      this.isRunning = true;
      this.stopRequested = false;
      this.batchStartTime = Date.now();
      this.currentIndex = 0;
      this.batchMode = mode;
      const label = mode === "confirm" ? "ยืนยันข้อมูล" : "ประมวลผลอัตรากำลัง";
      this.addLog(
        `เริ่ม${label} ${queue.length} สถานศึกษา ปี ${this.yearCalNow}`,
        "info"
      );

      for (let i = 0; i < queue.length; i++) {
        if (this.stopRequested) {
          this.addLog("หยุดกลางคัน", "warn");
          break;
        }
        this.currentIndex = i + 1;
        if (mode === "confirm") {
          await this.confirmSingleCollege(queue[i]);
        } else {
          await this.processSingleCollege(queue[i]);
        }
        await this.sleep(150);
      }

      this.isRunning = false;
      this.currentCollegeName = "";
      this.currentStep = "";
      const successCount =
        mode === "confirm"
          ? this.confirmedCount
          : this.calculatedCount + this.confirmedCount;
      this.addLog(
        `เสร็จสิ้น${label}: สำเร็จ ${successCount} / ผิดพลาด ${this.errorCount}`,
        "success"
      );
      this.snackbar = {
        show: true,
        icon: "mdi-check-circle",
        color: this.errorCount > 0 ? "warning" : "success",
        text: `${label}เสร็จสิ้น: สำเร็จ ${successCount} แห่ง, ผิดพลาด ${this.errorCount} แห่ง`
      };
    },

    async processSingleCollege(college) {
      const code = college.user_code;
      const name = college.college_name;
      const typeManpower = String(college.college_id_code_type_manpower);

      this.currentCollegeName = name;
      this.setStatus(code, {
        status: "running",
        step1: "pending",
        step2: "pending",
        step3: "pending",
        step4: this.collegeStatuses[code]?.step4 || "pending"
      });
      const t0 = Date.now();

      try {
        this.currentStep = "Step 1";
        this.addLog(
          `[${code}] ${name} — Step 1: บันทึกอัตรากำลังบุคลากร`,
          "step"
        );
        await this.step1_saveRateWorkG(code);
        this.setStatus(code, { step1: "ok" });
        if (this.stopRequested) throw new Error("หยุดโดยผู้ใช้");

        this.currentStep = "Step 2";
        this.addLog(`[${code}] ${name} — Step 2: ประมวลผลห้องเรียน`, "step");
        await this.step2_calcRooms(code, typeManpower);
        this.setStatus(code, { step2: "ok" });
        if (this.stopRequested) throw new Error("หยุดโดยผู้ใช้");

        this.currentStep = "Step 3";
        this.addLog(`[${code}] ${name} — Step 3: คำนวณอัตรากำลัง`, "step");
        const summary = await this.step3_calcManpower(code, typeManpower);
        this.setStatus(code, { step3: "ok", ...summary });

        const dur = ((Date.now() - t0) / 1000).toFixed(1);
        this.setStatus(code, {
          status: "calculated",
          duration: dur,
          errorMsg: null
        });
        this.addLog(
          `[${code}] ${name} — ✓ คำนวณเสร็จ (${dur}s) — ${this.criterionLabel(
            summary.incompleteAll
          )}`,
          "success"
        );

        if (this.autoConfirm && !this.stopRequested) {
          await this.confirmSingleCollege(college, true);
        }
      } catch (e) {
        const dur = ((Date.now() - t0) / 1000).toFixed(1);
        this.setStatus(code, {
          status: "error",
          duration: dur,
          errorMsg: e.message || "Unknown error"
        });
        this.addLog(`[${code}] ${name} — ERROR: ${e.message}`, "error");
      }
    },

    async confirmSingleCollege(college, chained = false) {
      const code = college.user_code;
      const name = college.college_name;
      const typeManpower = String(college.college_id_code_type_manpower);

      if (!chained) this.currentCollegeName = name;
      this.currentStep = "Step 4";
      this.setStatus(code, { step4: "pending" });

      try {
        this.addLog(
          `[${code}] ${name} — Step 4: ยืนยันการบันทึกข้อมูล`,
          "step"
        );
        await this.step4_confirmCollege(code, typeManpower);
        this.setStatus(code, {
          step4: "ok",
          status: "confirmed",
          errorMsg: null
        });
        this.addLog(`[${code}] ${name} — ✓ ยืนยันข้อมูลเรียบร้อย`, "success");
      } catch (e) {
        this.setStatus(code, {
          step4: "err",
          status: "error",
          errorMsg: e.message || "ยืนยันข้อมูลไม่สำเร็จ"
        });
        this.addLog(`[${code}] ${name} — ERROR (Step4): ${e.message}`, "error");
      }
    },

    async step4_confirmCollege(code, typeManpower) {
      const failed = [];
      const note = (name, res) => {
        if (!res?.data?.status) {
          const msg = res?.data?.message || res?.data?.msg;
          failed.push(msg ? `${name} (${msg})` : name);
          this.addLog(
            `[${code}] ${name} confirm ตอบกลับ: ${JSON.stringify(res?.data)}`,
            "warn"
          );
        }
      };

      const collegeRes = await this.$http.post(
        "rate_work_college.confirm.update.php",
        {
          ApiKey: this.ApiKey,
          rate_work_college_year: this.yearCalNow,
          rate_work_college_code: code,
          rate_work_college_status: "Confirm"
        }
      );
      note("rate_work_college", collegeRes);

      const roomRes = await this.$http.post(
        "rate_work_college_cal_room.confirm.update.php",
        {
          ApiKey: this.ApiKey,
          rate_work_ccr_year: this.yearCalNow,
          rate_work_ccr_code: code,
          rate_work_college_cal_room_status: "Confirm"
        }
      );
      note("rate_work_college_cal_room", roomRes);

      const gRes = await this.$http.post("rate_work_g.confirm.update.php", {
        ApiKey: this.ApiKey,
        rate_work_g_year: this.yearCalNow,
        rate_work_g_college_code: code,
        rate_work_g_status: "Confirm"
      });
      note("rate_work_g", gRes);

      if (String(typeManpower) === "6") {
        const scRes = await this.$http.post("rate_work_sc.confirm.update.php", {
          ApiKey: this.ApiKey,
          rate_sc_year: this.yearCalNow,
          rate_sc_college_code: code,
          rate_sc_status: "Confirm"
        });
        note("rate_work_sc", scRes);
      }

      if (failed.length) {
        throw new Error(`ยืนยันไม่สำเร็จที่: ${failed.join(", ")}`);
      }
    },

    async step1_saveRateWorkG(code) {
      const pRes = await this.$http.post("personnel_etc.std.php", {
        ApiKey: this.ApiKey,
        personnel_etc_college_code: code
      });
      const p = pRes.data || {};

      const existRes = await this.$http.post("rate_work_g.php", {
        ApiKey: this.ApiKey,
        rate_work_g_college_code: code,
        rate_work_g_year: this.yearCalNow
      });
      const hasExisting = !!(
        existRes.data && existRes.data.rate_work_g_college_code
      );

      const payload = {
        ApiKey: this.ApiKey,
        rate_work_g_year: this.yearCalNow,
        rate_work_g_college_code: code,
        rate_work_g_date_time: this.dateToday,
        rate_work_g_dr: this.int(p.countDirector),
        rate_work_g_sedr: this.int(p.countSe_director),
        rate_work_g_gs_ta: this.int(p.countTeach00),
        rate_work_g_gs_tb: this.int(p.countGov00),
        rate_work_g_gs_tc: this.int(p.countTem00),
        rate_work_g_is_ta: this.int(p.countTeach01),
        rate_work_g_is_tb: this.int(p.countGov01),
        rate_work_g_is_tc: this.int(p.countTem01),
        rate_work_g_cm_ta: this.int(p.countTeach02),
        rate_work_g_cm_tb: this.int(p.countGov02),
        rate_work_g_cm_tc: this.int(p.countTem02),
        rate_work_g_art_ta: this.int(p.countTeach03),
        rate_work_g_art_tb: this.int(p.countGov03),
        rate_work_g_art_tc: this.int(p.countTem03),
        rate_work_g_em_ta: this.int(p.countTeach04),
        rate_work_g_em_tb: this.int(p.countGov04),
        rate_work_g_em_tc: this.int(p.countTem04),
        rate_work_g_ag_ta: this.int(p.countTeach05),
        rate_work_g_ag_tb: this.int(p.countGov05),
        rate_work_g_ag_tc: this.int(p.countTem05),
        rate_work_g_fish_ta: this.int(p.countTeach06),
        rate_work_g_fish_tb: this.int(p.countGov06),
        rate_work_g_fish_tc: this.int(p.countTem06),
        rate_work_g_tour_ta: this.int(p.countTeach07),
        rate_work_g_tour_tb: this.int(p.countGov07),
        rate_work_g_tour_tc: this.int(p.countTem07),
        rate_work_g_textile_ta: this.int(p.countTeach08),
        rate_work_g_textile_tb: this.int(p.countGov08),
        rate_work_g_textile_tc: this.int(p.countTem08),
        rate_work_g_insy_ta: this.int(p.countTeach09),
        rate_work_g_insy_tb: this.int(p.countGov09),
        rate_work_g_insy_tc: this.int(p.countTem09),
        rate_work_g_entertain_ta: this.int(p.countTeach10),
        rate_work_g_entertain_tb: this.int(p.countGov10),
        rate_work_g_entertain_tc: this.int(p.countTem10),
        rate_work_g_navy_ta: this.int(p.countTeach11),
        rate_work_g_navy_tb: this.int(p.countGov11),
        rate_work_g_navy_tc: this.int(p.countTem11),
        rate_work_g_agriInno_ta: this.int(p.countTeach12),
        rate_work_g_agriInno_tb: this.int(p.countGov12),
        rate_work_g_agriInno_tc: this.int(p.countTem12),
        rate_work_g_sc_ta: this.int(p.countTeach66),
        rate_work_g_sc_tb: this.int(p.countGov66),
        rate_work_g_sc_tc: this.int(p.countTem66),
        rate_work_g_sup_ta: this.int(p.count38),
        rate_work_g_sup_tb: this.int(p.countgovernmentsp),
        rate_work_g_pte_ta: this.int(p.countPermanent),
        rate_work_g_pte_tb: this.int(p.countTemporary_job),
        rate_work_g_pte_tc: this.int(p.countTemporary)
      };

      const ep = hasExisting
        ? "rate_work_g.update.php"
        : "rate_work_g.insert.php";
      const r = await this.$http.post(ep, payload);
      if (!r.data?.status) throw new Error(`Step1 ${ep} failed`);
    },

    async step2_calcRooms(code, typeManpower) {
      const grRes = await this.$http.post("rate_work_college.php", {
        ApiKey: this.ApiKey,
        rate_work_college_code: code,
        rate_work_college_year: this.yearCalNow
      });
      const groups = Array.isArray(grRes.data) ? grRes.data : [];

      for (const item of groups) {
        if (this.stopRequested) break;
        const calc = this.calcRooms(item, typeManpower);
        const ep = item.cal_room
          ? "rate_work_college_cal_room.update.php"
          : "rate_work_college_cal_room.insert.php";
        await this.$http.post(ep, this.buildRoomPayload(item, calc));
      }

      if (!this.stopRequested) await this.step2_general(code);

      if (!this.stopRequested && typeManpower === "6")
        await this.step2_shortCourse(code);
    },

    async step2_general(code) {
      const r = await this.$http.post("rate_work_college.php", {
        ApiKey: this.ApiKey,
        rate_work_college_code: code,
        rate_work_college_year: this.yearCalNow,
        sum_all_general: "OK"
      });
      const gen = r.data;
      if (!gen?.rate_work_college_year) return;
      const c = this.calcGeneralRooms(gen);
      const ep = gen.cal_room
        ? "rate_work_college_cal_room.update.php"
        : "rate_work_college_cal_room.insert.php";
      const base = {
        ApiKey: this.ApiKey,
        rate_work_ccr_year: gen.rate_work_college_year,
        rate_work_ccr_code: gen.rate_work_college_code,
        rate_work_ccr_course_id: "0"
      };
      await this.$http.post(ep, {
        ...base,
        rate_work_ccr_id_class: "20",
        rate_work_ccr_l1_sum: gen.sum_branch_20_1,
        rate_work_ccr_l1_cal: c.r01,
        rate_work_ccr_l2_sum: gen.sum_branch_20_2,
        rate_work_ccr_l2_cal: c.r02,
        rate_work_ccr_l3_sum: gen.sum_branch_20_3,
        rate_work_ccr_l3_cal: c.r03,
        rate_work_ccr_all_sum: gen.sum_all_20,
        rate_work_ccr_all_cal: c.sumRoom20,
        rate_work_ccr_man_power: String(c.manPower20)
      });
      await this.$http.post(ep, {
        ...base,
        rate_work_ccr_id_class: "30",
        rate_work_ccr_l1_sum: gen.sum_branch_30_1,
        rate_work_ccr_l1_cal: c.r04,
        rate_work_ccr_l2_sum: gen.sum_branch_30_2,
        rate_work_ccr_l2_cal: c.r05,
        rate_work_ccr_l3_sum: "0",
        rate_work_ccr_l3_cal: "0",
        rate_work_ccr_all_sum: gen.sum_all_30,
        rate_work_ccr_all_cal: c.sumRoom30,
        rate_work_ccr_man_power: String(c.manPower30)
      });
    },

    async step2_shortCourse(code) {
      const listRes = await this.$http.post("rate_work_sc.php", {
        ApiKey: this.ApiKey,
        rate_sc_college_code: code,
        rate_sc_year: this.yearCalNow
      });
      const scList = Array.isArray(listRes.data) ? listRes.data : [];

      if (scList.length) {
        this.addLog(
          `[${code}] พบหลักสูตรระยะสั้น ${scList.length} รายการ — กำลังคำนวณห้องเรียน/ชั่วโมง`,
          "step"
        );
      }

      for (const course of scList) {
        if (this.stopRequested) break;
        const calc = this.calcShortCourse(course);
        await this.$http.post("rate_work_sc.update.php", {
          ...course,
          ApiKey: this.ApiKey,
          rate_sc_year: this.yearCalNow,
          rate_sc_college_code: code,
          rate_work_sc_sum: calc.sumStudents,
          rate_work_sc_date: this.dateToday,
          rate_work_sc_room_sum: calc.sumRoom,
          rate_work_sc_hours: calc.sumHours
        });
      }

      const r = await this.$http.post("rate_work_sc.php", {
        ApiKey: this.ApiKey,
        rate_sc_college_code: code,
        rate_sc_year: this.yearCalNow,
        cal_man_power: "ok"
      });
      const sc = r.data;
      if (!sc?.rate_sc_year) return;
      const hours = this.flt(sc.sc_hours_sum_all);
      const ep = sc.cal_room
        ? "rate_work_college_cal_room.update.php"
        : "rate_work_college_cal_room.insert.php";
      await this.$http.post(ep, {
        ApiKey: this.ApiKey,
        rate_work_ccr_year: sc.rate_sc_year,
        rate_work_ccr_code: sc.rate_sc_college_code,
        rate_work_ccr_course_id: "66",
        rate_work_ccr_id_class: "66",
        rate_work_ccr_l1_sum: "0",
        rate_work_ccr_l1_cal: "0",
        rate_work_ccr_l2_sum: "0",
        rate_work_ccr_l2_cal: "0",
        rate_work_ccr_l3_sum: "0",
        rate_work_ccr_l3_cal: "0",
        rate_work_ccr_all_sum: sc.sc_hours_sum_all,
        rate_work_ccr_all_cal: Math.round(hours / 1080),
        rate_work_ccr_man_power: String(hours / 828)
      });
    },

    roomBy30m(n) {
      const v = this.int(n);
      if (v < 15) return 0;
      if (v < 30) return 1;
      return (v / 30) % 1 < 1 / 3 ? Math.floor(v / 30) : Math.ceil(v / 30);
    },

    calcShortCourse(item) {
      let sumStudents = 0;
      let sumRoom = 0;
      for (let i = 1; i <= 25; i++) {
        const gen = this.int(item[`gen_${i}`]);
        sumStudents += gen;
        sumRoom += this.roomBy30m(gen);
      }
      const hoursTeaching = this.flt(item.hours_teaching);
      const sumHours = sumRoom * hoursTeaching;
      return { sumStudents, sumRoom, sumHours };
    },

    async step3_calcManpower(code, typeManpower) {
      const gRes = await this.$http.post("rate_work_g.php", {
        ApiKey: this.ApiKey,
        rate_work_g_college_code: code,
        rate_work_g_year: this.yearCalNow
      });
      const g = gRes.data;
      if (!g?.rate_work_g_college_code)
        throw new Error("ไม่พบ rate_work_g (Step3)");

      const qi = f => this.int(g[f]);
      const q = {
        dr: qi("rate_work_g_dr"),
        sedr: qi("rate_work_g_sedr"),
        gs_ta: qi("rate_work_g_gs_ta"),
        gs_tb: qi("rate_work_g_gs_tb"),
        is_ta: qi("rate_work_g_is_ta"),
        is_tb: qi("rate_work_g_is_tb"),
        cm_ta: qi("rate_work_g_cm_ta"),
        cm_tb: qi("rate_work_g_cm_tb"),
        art_ta: qi("rate_work_g_art_ta"),
        art_tb: qi("rate_work_g_art_tb"),
        em_ta: qi("rate_work_g_em_ta"),
        em_tb: qi("rate_work_g_em_tb"),
        ag_ta: qi("rate_work_g_ag_ta"),
        ag_tb: qi("rate_work_g_ag_tb"),
        fish_ta: qi("rate_work_g_fish_ta"),
        fish_tb: qi("rate_work_g_fish_tb"),
        tour_ta: qi("rate_work_g_tour_ta"),
        tour_tb: qi("rate_work_g_tour_tb"),
        textile_ta: qi("rate_work_g_textile_ta"),
        textile_tb: qi("rate_work_g_textile_tb"),
        insy_ta: qi("rate_work_g_insy_ta"),
        insy_tb: qi("rate_work_g_insy_tb"),
        entertain_ta: qi("rate_work_g_entertain_ta"),
        entertain_tb: qi("rate_work_g_entertain_tb"),
        navy_ta: qi("rate_work_g_navy_ta"),
        navy_tb: qi("rate_work_g_navy_tb"),
        agriInno_ta: qi("rate_work_g_agriInno_ta"),
        agriInno_tb: qi("rate_work_g_agriInno_tb"),
        sc_ta: qi("rate_work_g_sc_ta"),
        sc_tb: qi("rate_work_g_sc_tb"),
        sup_ta: qi("rate_work_g_sup_ta"),
        sup_tb: qi("rate_work_g_sup_tb")
      };
      q.sumteach_a =
        q.gs_ta +
        q.is_ta +
        q.cm_ta +
        q.art_ta +
        q.em_ta +
        q.ag_ta +
        q.fish_ta +
        q.tour_ta +
        q.textile_ta +
        q.insy_ta +
        q.entertain_ta +
        q.navy_ta +
        q.agriInno_ta +
        q.sc_ta;
      q.sumteach_b =
        q.gs_tb +
        q.is_tb +
        q.cm_tb +
        q.art_tb +
        q.em_tb +
        q.ag_tb +
        q.fish_tb +
        q.tour_tb +
        q.textile_tb +
        q.insy_tb +
        q.entertain_tb +
        q.navy_tb +
        q.agriInno_tb +
        q.sc_tb;
      q.sumall =
        q.dr + q.sedr + q.sup_ta + q.sup_tb + q.sumteach_a + q.sumteach_b;

      const drRes = await this.$http.post("rate_work_college_cal_room.php", {
        ApiKey: this.ApiKey,
        rate_work_ccr_code: code,
        rate_work_ccr_year: this.yearCalNow,
        cal_room_for_dr: "Ok"
      });
      const calRoomDr = this.int(drRes.data?.rate_work_cal_room_dr);

      const teachRes = await this.$http.post("rate_work_college_cal_room.php", {
        ApiKey: this.ApiKey,
        rate_work_ccr_code: code,
        rate_work_ccr_year: this.yearCalNow,
        cal_room_for_teach: "Ok"
      });
      const ct = teachRes.data || {};
      const n = f => this.flt(ct[f]);

      const criterion_dr = calRoomDr >= 1 ? 1 : 0;
      const incomplete_dr = q.dr - criterion_dr;
      let criterion_sec_dr = 0;
      if (calRoomDr >= 37) criterion_sec_dr = 4;
      else if (calRoomDr >= 26) criterion_sec_dr = 3;
      else if (calRoomDr >= 16) criterion_sec_dr = 2;
      else if (calRoomDr >= 3) criterion_sec_dr = 1;
      const incomplete_sec_dr = q.sedr - criterion_sec_dr;

      const rate_std_00 = n("rate_std_00");
      const cc = (rate, ta, tb) => {
        const off = parseFloat(((90 * rate) / 100).toFixed(2));
        const civ = parseFloat(((10 * rate) / 100).toFixed(2));
        return { off, civ, inc: parseFloat((ta + tb - off - civ).toFixed(2)) };
      };
      const c00 = cc(n("rate_00"), q.gs_ta, q.gs_tb);
      const c01 = cc(n("rate_01"), q.is_ta, q.is_tb);
      const c02 = cc(n("rate_02"), q.cm_ta, q.cm_tb);
      const c03 = cc(n("rate_03"), q.art_ta, q.art_tb);
      const c04 = cc(n("rate_04"), q.em_ta, q.em_tb);
      const c05 = cc(n("rate_05"), q.ag_ta, q.ag_tb);
      const c06 = cc(n("rate_06"), q.fish_ta, q.fish_tb);
      const c07 = cc(n("rate_07"), q.tour_ta, q.tour_tb);
      const c08 = cc(n("rate_08"), q.textile_ta, q.textile_tb);
      const c09 = cc(n("rate_09"), q.insy_ta, q.insy_tb);
      const c10 = cc(n("rate_10"), q.entertain_ta, q.entertain_tb);
      const c11 = cc(n("rate_11"), q.navy_ta, q.navy_tb);
      const c12 = cc(n("rate_12"), q.agriInno_ta, q.agriInno_tb);
      const c66 =
        typeManpower === "6"
          ? cc(n("rate_66"), q.sc_ta, q.sc_tb)
          : { off: 0, civ: 0, inc: 0 };

      let official_rate_sup = 0;
      if (rate_std_00 > 479)
        official_rate_sup = Math.floor(
          (50 * ((8.5 * calRoomDr + 24.5) / 30)) / 100
        );
      const civil_servant_rate_sub =
        rate_std_00 < 480
          ? Math.round((8.5 * calRoomDr + 24.5) / 30)
          : Math.round((8.5 * calRoomDr + 24.5) / 30 - official_rate_sup);
      const incomplete_rate_sup =
        q.sup_ta + q.sup_tb - (civil_servant_rate_sub + official_rate_sup);

      const rate_sum_man_all = Math.round(n("rate_sum_man_all"));
      const criterion_sec_teach = Math.floor((90 * rate_sum_man_all) / 100);
      const incomplete_rate_teach = Math.round(
        rate_sum_man_all - criterion_sec_teach
      );
      const incomplete_rate_teach_result =
        q.sumteach_a +
        q.sumteach_b -
        (criterion_sec_teach + incomplete_rate_teach);
      const rate_work_all_criterion = Math.round(
        criterion_dr +
          criterion_sec_dr +
          criterion_sec_teach +
          incomplete_rate_teach +
          official_rate_sup +
          civil_servant_rate_sub
      );
      const incomplete_all = q.sumall - rate_work_all_criterion;

      const up = {
        ApiKey: this.ApiKey,
        rate_work_g_year: this.yearCalNow,
        rate_work_g_college_code: code,
        rate_work_g_status: "Save",
        rate_criterion_dr: criterion_dr,
        rate_criterion_incomplete_dr: incomplete_dr,
        rate_criterion_sedr: criterion_sec_dr,
        rate_criterion_incomplete_sedr: incomplete_sec_dr,
        rate_teach_official_all: q.sumteach_a,
        rate_teach_civil_all: q.sumteach_b,
        rate_criterion_teach_all: criterion_sec_teach,
        rate_criterion_teach_alls: incomplete_rate_teach,
        rate_criterion_incomplete_teach_all: incomplete_rate_teach_result,
        rate_man_power_teach_all: rate_sum_man_all,
        rate_room_cal_all: calRoomDr,
        rate_work_g_gs_ta_official: c00.off,
        rate_work_g_gs_tb_civil: c00.civ,
        rate_work_g_gs_incomplete: c00.inc,
        rate_work_g_is_ta_official: c01.off,
        rate_work_g_is_tb_civil: c01.civ,
        rate_work_g_is_incomplete: c01.inc,
        rate_work_g_cm_ta_official: c02.off,
        rate_work_g_cm_tb_civil: c02.civ,
        rate_work_g_cm_incomplete: c02.inc,
        rate_work_g_art_ta_official: c03.off,
        rate_work_g_art_tb_civil: c03.civ,
        rate_work_g_art_incomplete: c03.inc,
        rate_work_g_em_ta_official: c04.off,
        rate_work_g_em_tb_civil: c04.civ,
        rate_work_g_em_incomplete: c04.inc,
        rate_work_g_ag_ta_official: c05.off,
        rate_work_g_ag_tb_civil: c05.civ,
        rate_work_g_ag_incomplete: c05.inc,
        rate_work_g_fish_ta_official: c06.off,
        rate_work_g_fish_tb_civil: c06.civ,
        rate_work_g_fish_incomplete: c06.inc,
        rate_work_g_tour_ta_official: c07.off,
        rate_work_g_tour_tb_civil: c07.civ,
        rate_work_g_tour_incomplete: c07.inc,
        rate_work_g_textile_ta_official: c08.off,
        rate_work_g_textile_tb_civil: c08.civ,
        rate_work_g_textile_incomplete: c08.inc,
        rate_work_g_insy_ta_official: c09.off,
        rate_work_g_insy_tb_civil: c09.civ,
        rate_work_g_insy_incomplete: c09.inc,
        rate_work_g_entertain_ta_official: c10.off,
        rate_work_g_entertain_tb_civil: c10.civ,
        rate_work_g_entertain_incomplete: c10.inc,
        rate_work_g_navy_ta_official: c11.off,
        rate_work_g_navy_tb_civil: c11.civ,
        rate_work_g_navy_incomplete: c11.inc,
        rate_work_g_agriInno_ta_official: c12.off,
        rate_work_g_agriInno_tb_civil: c12.civ,
        rate_work_g_agriInno_incomplete: c12.inc,
        rate_work_g_sc_ta_official: c66.off,
        rate_work_g_sc_tb_civil: c66.civ,
        rate_work_g_sc_incomplete: c66.inc,
        rate_work_g_sup_ta_official: official_rate_sup,
        rate_work_g_sup_tb_civil: civil_servant_rate_sub,
        rate_work_g_sup_incomplete: incomplete_rate_sup,
        rate_man_power_all: q.sumall,
        rate_criterion_all: rate_work_all_criterion,
        rate_criterion_incomplete_all: incomplete_all
      };

      const rUp = await this.$http.post("rate_work_g_cal.update.php", up);
      if (!rUp.data?.status) {
        this.addLog(
          `[${code}] rate_work_g_cal.update.php status=false (อาจ OK)`,
          "warn"
        );
      }

      return {
        criterionAll: rate_work_all_criterion,
        manpowerAll: q.sumall,
        incompleteAll: incomplete_all
      };
    },

    buildRoomPayload(item, c) {
      const ins = {
        ApiKey: this.ApiKey,
        rate_work_ccr_year: item.rate_work_college_year,
        rate_work_ccr_code: item.rate_work_college_code,
        rate_work_ccr_course_id: item.rate_work_course_id,
        rate_work_ccr_id_class: item.rate_work_college_id_class
      };
      if (item.rate_work_college_id_class == "20") {
        ins.rate_work_ccr_l1_sum = item.std_level_1 || 0;
        ins.rate_work_ccr_l1_cal = c.r01;
        ins.rate_work_ccr_l2_sum = item.std_level_2 || 0;
        ins.rate_work_ccr_l2_cal = c.r02;
        ins.rate_work_ccr_l3_sum = item.std_level_3 || 0;
        ins.rate_work_ccr_l3_cal = c.r03;
        ins.rate_work_ccr_all_sum =
          this.int(item.std_level_1) +
          this.int(item.std_level_2) +
          this.int(item.std_level_3);
      } else {
        ins.rate_work_ccr_l1_sum = item.std_level_1 || 0;
        ins.rate_work_ccr_l1_cal = c.r01;
        ins.rate_work_ccr_l2_sum = item.std_level_2 || 0;
        ins.rate_work_ccr_l2_cal = c.r02;
        ins.rate_work_ccr_l3_sum = 0;
        ins.rate_work_ccr_l3_cal = 0;
        ins.rate_work_ccr_all_sum =
          this.int(item.std_level_1) + this.int(item.std_level_2);
      }
      ins.rate_work_ccr_all_cal = parseInt(c.sumRoom);
      ins.rate_work_ccr_man_power = String(c.manPower);
      return ins;
    },

    calcRooms(item, typeManpower) {
      const ut = String(typeManpower),
        ci = String(item.rate_work_course_id),
        cl = String(item.rate_work_college_id_class);
      const l1 = isNaN(item.std_level_1) ? 0 : Number(item.std_level_1);
      const l2 = isNaN(item.std_level_2) ? 0 : Number(item.std_level_2);
      const l3 = isNaN(item.std_level_3) ? 0 : Number(item.std_level_3);
      let r01 = 0,
        r02 = 0,
        r03 = 0,
        sumRoom = 0;
      const r30 = (n, min = 1) =>
        n < min
          ? 0
          : n < 30
          ? 1
          : (n / 30) % 1 < 1 / 3
          ? Math.floor(n / 30)
          : Math.ceil(n / 30);
      const r30m = n =>
        n < 15
          ? 0
          : n < 30
          ? 1
          : (n / 30) % 1 < 1 / 3
          ? Math.floor(n / 30)
          : Math.ceil(n / 30);
      const r40 = (n, min = 1) =>
        n < min
          ? 0
          : n < 40
          ? 1
          : (n / 40) % 1 < 0.25
          ? Math.floor(n / 40)
          : Math.ceil(n / 40);
      const r40m = n =>
        n < 15
          ? 0
          : n < 40
          ? 1
          : (n / 40) % 1 < 0.25
          ? Math.floor(n / 40)
          : Math.ceil(n / 40);
      if (ut === "1") {
        if (cl === "20") {
          if (["1", "8", "11",  "18", "19"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
            r03 = r30(l3);
          } else if (ci === "9") {
            r01 = r40(l1);
            r02 = r40(l2);
            r03 = r40(l3);
          } else if (["2", "7"].includes(ci)) {
            r01 = r40m(l1);
            r02 = r40m(l2);
            r03 = r40m(l3);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
            r03 = r30m(l3);
          }
          sumRoom = r01 + r02 + r03;
        } else {
          if (["1", "8", "9", "11",  "18", "19"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
          }
          sumRoom = r01 + r02;
        }
      } else if (ut === "2") {
        if (cl === "20") {
          if (["1", "8",  "18", "19"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
            r03 = r30(l3);
          } else if (ci === "2") {
            r01 = r40(l1);
            r02 = r40(l2);
            r03 = r40(l3);
          } else if (["9", "7"].includes(ci)) {
            r01 = r40m(l1);
            r02 = r40m(l2);
            r03 = r40m(l3);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
            r03 = r30m(l3);
          }
          sumRoom = r01 + r02 + r03;
        } else {
          if (["1", "2", "8",  "18", "19"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
          }
          sumRoom = r01 + r02;
        }
      } else if (ut === "3") {
        if (cl === "20") {
          if (["2", "7"].includes(ci)) {
            r01 = r40(l1);
            r02 = r40(l2);
            r03 = r40(l3);
          } else if (ci === "9") {
            r01 = r40m(l1);
            r02 = r40m(l2);
            r03 = r40m(l3);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
            r03 = r30m(l3);
          }
          sumRoom = r01 + r02 + r03;
        } else {
          if (["2", "7"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
          }
          sumRoom = r01 + r02;
        }
      } else if (ut === "4") {
        if (cl === "20") {
          if (ci === "2") {
            r01 = r40(l1);
            r02 = r40(l2);
            r03 = r40(l3);
          } else if (["9", "7"].includes(ci)) {
            r01 = r40m(l1);
            r02 = r40m(l2);
            r03 = r40m(l3);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
            r03 = r30m(l3);
          }
          sumRoom = r01 + r02 + r03;
        } else {
          if (ci === "2") {
            r01 = r30(l1);
            r02 = r30(l2);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
          }
          sumRoom = r01 + r02;
        }
      } else if (ut === "5") {
        if (cl === "20") {
          if (["3", "10", "16", "20"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
            r03 = r30(l3);
          } else if (["2", "7", "9"].includes(ci)) {
            r01 = r40m(l1);
            r02 = r40m(l2);
            r03 = r40m(l3);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
            r03 = r30m(l3);
          }
          sumRoom = r01 + r02 + r03;
        } else {
          if (["3", "10", "16", "20"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
          }
          sumRoom = r01 + r02;
        }
      } else if (ut === "6") {
        if (cl === "20") {
          if (["2", "7", "9"].includes(ci)) {
            r01 = r40m(l1);
            r02 = r40m(l2);
            r03 = r40m(l3);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
            r03 = r30m(l3);
          }
          sumRoom = r01 + r02 + r03;
        } else {
          r01 = r30m(l1);
          r02 = r30m(l2);
          sumRoom = r01 + r02;
        }
      } else if (ut === "7") {
        if (cl === "20") {
          if (["2", "7"].includes(ci)) {
            r01 = r40(l1);
            r02 = r40(l2);
            r03 = r40(l3);
          } else if (["4", "15"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
            r03 = r30(l3);
          } else if (ci === "9") {
            r01 = r40m(l1);
            r02 = r40m(l2);
            r03 = r40m(l3);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
            r03 = r30m(l3);
          }
          sumRoom = r01 + r02 + r03;
        } else {
          if (["2", "4", "7", "15"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
          }
          sumRoom = r01 + r02;
        }
      } else if (ut === "8") {
        if (cl === "20") {
          if (["1", "8",  "18", "19"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
            r03 = r30(l3);
          } else if (["7", "9"].includes(ci)) {
            r01 = r40m(l1);
            r02 = r40m(l2);
            r03 = r40m(l3);
          } else if (ci === "2") {
            r01 = r40(l1);
            r02 = r40(l2);
            r03 = r40(l3);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
            r03 = r30m(l3);
          }
          sumRoom = r01 + r02 + r03;
        } else {
          if (["1", "2", "8",  "18", "19"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
          }
          sumRoom = r01 + r02;
        }
      } else if (ut === "9") {
        if (cl === "20") {
          if (["5", "6", "12", "17", "20"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
            r03 = r30(l3);
          } else if (["2", "7", "9"].includes(ci)) {
            r01 = r40m(l1);
            r02 = r40m(l2);
            r03 = r40m(l3);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
            r03 = r30m(l3);
          }
          sumRoom = r01 + r02 + r03;
        } else {
          if (["5", "6", "12", "17", "20"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
          }
          sumRoom = r01 + r02;
        }
      } else if (ut === "10") {
        if (cl === "20") {
          if (["3", "10", "16", "20"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
            r03 = r30(l3);
          } else if (["2", "7", "9"].includes(ci)) {
            r01 = r40m(l1);
            r02 = r40m(l2);
            r03 = r40m(l3);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
            r03 = r30m(l3);
          }
          sumRoom = r01 + r02 + r03;
        } else {
          if (["3", "10", "16", "20"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
          }
          sumRoom = r01 + r02;
        }
      } else if (ut === "11") {
        if (cl === "20") {
          if (["1", "8",  "18", "19"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
            r03 = r30(l3);
          } else if (["2", "7", "9"].includes(ci)) {
            r01 = r40m(l1);
            r02 = r40m(l2);
            r03 = r40m(l3);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
            r03 = r30m(l3);
          }
          sumRoom = r01 + r02 + r03;
        } else {
          if (["1", "8",  "18", "19"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
          }
          sumRoom = r01 + r02;
        }
      } else if (ut === "12") {
        if (cl === "20") {
          if (["6", "11", "17"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
            r03 = r30(l3);
          } else if (["2", "7", "9"].includes(ci)) {
            r01 = r40m(l1);
            r02 = r40m(l2);
            r03 = r40m(l3);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
            r03 = r30m(l3);
          }
          sumRoom = r01 + r02 + r03;
        } else {
          if (["6", "11", "17"].includes(ci)) {
            r01 = r30(l1);
            r02 = r30(l2);
          } else {
            r01 = r30m(l1);
            r02 = r30m(l2);
          }
          sumRoom = r01 + r02;
        }
      }
      const sp = ["3", "5", "6", "11"];
      const manPower = sp.includes(ci)
        ? cl === "20"
          ? (21 * sumRoom) / 15
          : (25 * sumRoom) / 10
        : cl === "20"
        ? (21 * sumRoom) / 18
        : (25 * sumRoom) / 15;
      return { r01, r02, r03, sumRoom, manPower };
    },

    calcGeneralRooms(gen) {
      const n = v => (isNaN(v) ? 0 : Number(v));
      const c40 = v =>
        v < 1
          ? 0
          : v < 40
          ? 1
          : (v / 40) % 1 < 0.25
          ? Math.floor(v / 40)
          : Math.ceil(v / 40);
      const c30 = v =>
        v < 1
          ? 0
          : v < 30
          ? 1
          : (v / 30) % 1 < 1 / 3
          ? Math.floor(v / 30)
          : Math.ceil(v / 30);
      const r01 = c40(n(gen.sum_branch_20_1)),
        r02 = c40(n(gen.sum_branch_20_2)),
        r03 = c40(n(gen.sum_branch_20_3));
      const r04 = c30(n(gen.sum_branch_30_1)),
        r05 = c30(n(gen.sum_branch_30_2));
      const s20 = r01 + r02 + r03,
        s30 = r04 + r05;
      return {
        r01,
        r02,
        r03,
        r04,
        r05,
        sumRoom20: s20,
        sumRoom30: s30,
        manPower20: (7 * s20) / 18,
        manPower30: (6 * s30) / 15
      };
    }
  }
};
</script>

<style scoped>
.text_google {
  font-family: "Noto Sans Thai", sans-serif;
}
</style>
